/**
 * Dropship sourcing (B16-V4, D54, D70): the reseller's margin and price on a
 * vendor's product. Pure: no I/O, no React. Mirrors lib/supply/pricing.js and
 * lib/supply/sourcing.js on the server (tests/unit/dropship-sourcing.test.js
 * checks they agree); the server decides, against the vendor's live terms.
 */
import { maxRetail, MAX_CAP_PCT } from './supply';
import { rupeesToPaise } from './money';

export { maxRetail };
export const MAX_MARGIN_PCT = MAX_CAP_PCT; // one limit for caps and margins (D88)

const MARGIN_RE = /^\d+(\.\d{1,2})?$/;

/** Retail price for a margin % on W, to the paisa. */
export const retailFor = (wholesalePaise, marginPct) => Math.round(Number(wholesalePaise) * (1 + Number(marginPct) / 100));

/** Margin % a price gives on W, two decimals. */
export const marginFor = (W, R) => Math.round(((Number(R) / Number(W)) - 1) * 10000) / 100;

/** Is R allowed for this offer? → null when fine, else a message. */
export function retailProblem(wholesalePaise, capPct, retailPaise) {
  const W = Number(wholesalePaise);
  const R = Number(retailPaise);
  if (!Number.isInteger(R) || R < W) return 'At least the wholesale price';
  const max = maxRetail(W, capPct);
  if (max != null && R > max) return `The supplier allows at most ${Number(capPct)}% margin`;
  return null;
}

/** A margin % typed in a form → error message or null. */
export function marginError(v) {
  if (!MARGIN_RE.test(String(v ?? '').trim())) return 'A margin % like 20 or 12.5';
  if (Number(v) > MAX_MARGIN_PCT) return `At most ${MAX_MARGIN_PCT}%`;
  return null;
}

/** The highest margin % a set of offers allows together (the tightest cap), or null if none are capped. */
export function tightestCap(offers) {
  const caps = offers.map((o) => o.max_margin_pct).filter((c) => c != null).map(Number);
  return caps.length ? Math.min(...caps) : null;
}

/** Reprice form: { mode: 'margin'|'price', margin, price } → errors ({ field: message }). */
export function validateReprice(f, wholesalePaise, capPct) {
  if (f.mode === 'price') {
    const R = rupeesToPaise(f.price);
    if (!R) return { price: 'Enter your price in rupees' };
    const p = retailProblem(wholesalePaise, capPct, R);
    return p ? { price: p } : {};
  }
  const m = marginError(f.margin);
  if (m) return { margin: m };
  const p = retailProblem(wholesalePaise, capPct, retailFor(wholesalePaise, f.margin));
  return p ? { margin: p } : {};
}

export function repricePayload(id, f) {
  return f.mode === 'price' ? { id, price_paise: rupeesToPaise(f.price) } : { id, margin_pct: Number(f.margin) };
}

/** What a reseller may charge for an offer, for the table. */
export function chargeRange(wholesalePaise, capPct, fmt) {
  const max = maxRetail(wholesalePaise, capPct);
  return max == null ? `${fmt(wholesalePaise)} or more` : `up to ${fmt(max)}`;
}

/** Import results → a one-line summary. */
export function importSummary(results) {
  const n = (k) => results.filter((r) => r.result === k).length;
  const parts = [
    n('imported') && `${n('imported')} imported as drafts`,
    n('exists') && `${n('exists')} already in your catalog`,
    n('invalid') && `${n('invalid')} above the supplier's margin cap`,
    n('not_found') && `${n('not_found')} no longer available`,
  ].filter(Boolean);
  return parts.join(', ') || 'Nothing imported';
}
